import _ from 'lodash';
import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table';

// ICONS
import { ChevronRight, Search } from '@sparkpost/matchbox-icons';

// COMPONENTS
import { Empty } from 'src/components';
import { ScreenReaderExclude } from 'src/components/a11y';
import Pagination from 'src/components/collection/Pagination';
import { PageLink } from 'src/components/links';
import {
  Box,
  Button,
  Panel,
  ScreenReaderOnly,
  Stack,
  Table,
  Text,
  TextField
} from 'src/components/matchbox';
import { TranslatableText } from 'src/components/text';

// HOOKS
import { usePageFilters } from 'src/hooks';

// CONSTANTS
import { METRICS } from 'src/pages/alerts/constants/formConstants';

// REDUCERS
import TableFilters, {
  reducer as tableFiltersReducer
} from 'src/pages/domains/components/TableFilters';

// STYLES
import {
  FiltersBar,
  Label,
  PriorityHighTag,
  PriorityLowTag,
  PriorityMediumTag,
  PriorityWrapper,
  StyledIcon,
  TableWrapper
} from './styles';

const filterOptions = [
  {
    label: 'Date Triggered (Newest - Oldest)',
    value: 'last_triggered_timestamp_desc',
    id: 'last_triggered_timestamp',
    desc: true
  },
  {
    label: 'Date Triggered (Oldest - Newest)',
    value: 'last_triggered_timestamp_asc',
    id: 'last_triggered_timestamp',
    desc: false
  },
  {
    label: 'Alert Name (A - Z)',
    value: 'name_asc',
    id: 'name',
    desc: false
  },
  {
    label: 'Alert Name (Z - A)',
    value: 'name_desc',
    id: 'name',
    desc: true
  }
];

function getColumns() {
  return [
    {
      label: 'Alert Name',
      sortKey: 'name',
      width: '35%',
      sortType: 'basic',
      filter: 'fuzzyText',
      screenReaderOnly: false
    },
    {
      label: 'Subaccount',
      sortKey: 'subaccount',
      screenReaderOnly: true
    },
    {
      label: 'Metric',
      sortKey: 'metric',
      screenReaderOnly: true
    },
    { label: 'Threshold Value', sortKey: 'threshold', sortType: 'basic', screenReaderOnly: false },
    { label: 'Priority', sortKey: 'priority', screenReaderOnly: false },
    {
      label: 'Last Triggered',
      sortKey: 'last_triggered_timestamp',
      sortType: 'basic',
      screenReaderOnly: false
    },
    {
      label: 'View in Analytics',
      sortKey: 'viewInAnalytics',
      disableSortBy: true,
      screenReaderOnly: false
    }
  ];
}

const pageFilters = {
  alert: {},
  priority: {},
  sortBy: {},
  tab: {}
};

const getAlertLink = (alert) => {
  return `/alerts/details/${alert.id}`;
};

const Cell = ({ cell }) => {
  const alert = cell.row.original;

  const subaccount = alert.subaccounts;
  const metric = METRICS[alert.metric];

  if (cell.column.sortKey === 'name')
    return (
      <Box minWidth="140px">
        <Stack space="0">
          <PageLink to={`/alerts/details/${alert.id}`}>{alert.name}</PageLink>
          <ScreenReaderExclude>
            <Text>
              Metric: <TranslatableText>{metric}</TranslatableText>
            </Text>
            <Text>Subaccount: {subaccount}</Text>
          </ScreenReaderExclude>
        </Stack>
      </Box>
    );

  if (cell.column.sortKey === 'threshold')
    return (
      <Box minWidth="100px">
        <Stack space="100" align="right">
          <Label>Bounce Rate</Label>
          <Text>{alert.threshold_evaluator.value}</Text>
        </Stack>
      </Box>
    );

  if (cell.column.sortKey === 'priority')
    return _.sample([<PriorityHighTag />, <PriorityMediumTag />, <PriorityLowTag />]);

  if (cell.column.sortKey === 'last_triggered_timestamp')
    return (
      <Box minWidth="150px">
        <Stack space="100">
          <Label>Date Triggered</Label>
          <Text>{alert.last_triggered_formatted || 'Never Triggered'}</Text>
        </Stack>
      </Box>
    );

  if (cell.column.sortKey === 'viewInAnalytics')
    return (
      <Button variant="minimal" to={getAlertLink(alert)}>
        <TranslatableText>View in Analytics</TranslatableText>
        <StyledIcon>
          <ChevronRight />
        </StyledIcon>
      </Button>
    );

  // screen reader only
  if (cell.column.sortKey === 'subaccount')
    return <ScreenReaderOnly>Subaccount: {subaccount}</ScreenReaderOnly>;

  if (cell.column.sortKey === 'metric')
    return <ScreenReaderOnly>Metric: {metric}</ScreenReaderOnly>;

  return cell.value || '';
};

export default function RecentIncidents({ alerts }) {
  const filterPlaceholder = useMemo(() => _.sample(alerts)?.name || 'Alert name', [alerts]);
  const { filters, updateFilters } = usePageFilters(pageFilters);
  const [alertQuery, setAlertQuery] = useState('');
  const [selectSortBy, setSelectSortBy] = useState(
    filters.sortBy || 'last_triggered_timestamp_desc'
  );

  const [priorityState, dispatchPriority] = useReducer(tableFiltersReducer, {
    checkboxes: [
      {
        label: 'Select All',
        name: 'selectAll',
        isChecked: false
      },
      {
        label: 'High Priority',
        name: 'highPriority',
        isChecked: false
      },
      {
        label: 'Medium Priority',
        name: 'mediumPriority',
        isChecked: false
      },
      {
        label: 'Low Priority',
        name: 'lowPriority',
        isChecked: false
      }
    ]
  });

  const columnsMemoized = useMemo(
    () =>
      getColumns().map((col) => ({
        ...col,
        accessor: col.sortKey,
        Cell
      })),
    []
  );

  const data = useMemo(() => {
    return alerts.map((alert) => {
      return {
        ...alert
      };
    });
  }, [alerts]);

  const {
    headerGroups,
    getTableProps,
    getTableBodyProps,
    rows,
    page,
    prepareRow,
    setPageSize,
    state,
    gotoPage,
    setSortBy,
    setGlobalFilter
  } = useTable(
    {
      columns: columnsMemoized,
      data,
      initialState: {
        sortBy: [
          {
            id: 'last_triggered_timestamp',
            desc: true
          }
        ]
      }
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const handleSearch = (e) => {
    setAlertQuery(e.target.value);
  };

  useEffect(() => {
    setGlobalFilter(alertQuery);
    updateFilters({ ...filters, alert: alertQuery });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertQuery]);

  const handlePriorityChange = (e) => {
    const { target } = e;
    dispatchPriority({ type: 'TOGGLE', name: target.name }); // NOTE: updates checkbox state
  };

  useEffect(() => {
    const priority = [];

    priorityState.checkboxes
      .filter((priority) => priority.name !== 'selectAll' && priority.isChecked)
      .forEach((priorityItem) => priority.push(priorityItem.name));

    // apply the filters with setSortBy
    // const enabledFilters = priorityState.checkboxes.filter(
    //   ({ isChecked, name }) => name !== 'selectAll' && isChecked,
    // );

    updateFilters({ ...filters, priority });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priorityState]);

  const handleSortSelectChange = (e) => {
    const { currentTarget } = e;
    setSelectSortBy(currentTarget.value);
  };

  useEffect(() => {
    const filter = filterOptions.find((option) => option.value === selectSortBy);

    setSortBy([
      {
        id: filter.id,
        desc: filter.desc
      }
    ]);
    updateFilters({ ...filters, sortBy: selectSortBy });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectSortBy]);

  // initialize filters from url
  useEffect(() => {
    if (filters.alert) setAlertQuery(filters.alert);
    if (filters.priority && filters.priority.length) {
      const priority = typeof filters.priority === 'string' ? [filters.priority] : filters.priority;
      priority.forEach((priority) => {
        dispatchPriority({ type: 'TOGGLE', name: priority });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const originalRows = useMemo(() => rows.map((row) => row.original), [rows]);

  const table = (
    <Panel mb="500">
      <Panel.Section p="0">
        <TableWrapper>
          <Table {...getTableProps()} title="Alerts: Recent Incidents">
            <ScreenReaderOnly as="thead">
              {headerGroups.map((headerGroup) => {
                return (
                  <Table.Row header {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => {
                      return (
                        <Table.HeaderCell {...column.getHeaderProps(column.getSortByToggleProps())}>
                          {column.render('label')}
                        </Table.HeaderCell>
                      );
                    })}
                  </Table.Row>
                );
              })}
            </ScreenReaderOnly>

            <tbody {...getTableBodyProps()}>
              {page.length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan="100%" p="0">
                    <Empty message="No results found." />
                  </Table.Cell>
                </Table.Row>
              )}

              {page.map((row, i) => {
                prepareRow(row);
                return (
                  <Table.Row {...row.getRowProps()}>
                    {row.cells.map((cell) => {
                      return (
                        <Table.Cell {...cell.getCellProps()}>{cell.render('Cell')}</Table.Cell>
                      );
                    })}
                  </Table.Row>
                );
              })}
            </tbody>
          </Table>
        </TableWrapper>
      </Panel.Section>
    </Panel>
  );

  return (
    <>
      <Panel>
        <Panel.Section>
          <FiltersBar>
            <TextField
              id="filter-alert"
              maxWidth="inherit"
              label="Filter Alerts"
              prefix={<Search />}
              onChange={handleSearch}
              value={alertQuery}
              placeholder={`e.g. ${filterPlaceholder}`}
            />

            <PriorityWrapper>
              <TableFilters.StatusPopover
                label="Priority Level"
                placeholder="Select"
                checkboxes={priorityState.checkboxes}
                onCheckboxChange={handlePriorityChange}
              />
            </PriorityWrapper>

            <TableFilters.SortSelect
              defaultValue={selectSortBy}
              options={filterOptions}
              onChange={handleSortSelectChange}
            />
          </FiltersBar>
        </Panel.Section>
      </Panel>

      {table}

      <Pagination
        data={originalRows}
        perPage={state.pageSize}
        currentPage={state.pageIndex + 1}
        onPageChange={gotoPage}
        onPerPageChange={setPageSize}
      />
    </>
  );
}
